/**
 * WebRTC Configuration
 * Centralized configuration for STUN/TURN servers and connection settings
 */

export interface WebRTCConfig {
  iceServers: RTCIceServer[];
  constraints: MediaStreamConstraints;
  connectionOptions: RTCConfiguration;
}

export const DEFAULT_WEBRTC_CONFIG: WebRTCConfig = {
  iceServers: [
    {
      urls: "stun:stun.l.google.com:19302"
    },
    {
      urls: "stun:stun1.l.google.com:19302"
    },
    {
      urls: "stun:stun2.l.google.com:19302"
    }
  ],
  constraints: {
    audio: {
      echoCancellation: true,
      noiseSuppression: true,
      autoGainControl: true,
      channelCount: 1,
      sampleRate: 16000,
    },
    video: false
  },
  connectionOptions: {
    iceServers: [
      {
        urls: "stun:stun.l.google.com:19302"
      }
    ],
    iceCandidatePoolSize: 10
  }
};

export const AUDIO_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true,
    channelCount: 1,
    sampleRate: 16000,
    sampleSize: 16,
  },
  video: false
};

export const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  audio: {
    echoCancellation: true,
    noiseSuppression: true,
    autoGainControl: true
  },
  video: {
    width: { ideal: 1280 },
    height: { ideal: 720 },
    frameRate: { ideal: 30 }
  }
};

/**
 * Connection states for WebRTC peer connection
 */
export enum ConnectionState {
  NEW = 'new',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  DISCONNECTED = 'disconnected',
  FAILED = 'failed',
  CLOSED = 'closed'
}

/**
 * Call states for the calling system
 */
export enum CallState {
  IDLE = 'idle',
  INITIATING = 'initiating',
  RINGING = 'ringing',
  CONNECTING = 'connecting',
  CONNECTED = 'connected',
  RECONNECTING = 'reconnecting',
  ENDED = 'ended',
  ERROR = 'error'
}

/**
 * Call events for state management
 */
export interface CallEvent {
  type: 'call:initiated' | 'call:accepted' | 'call:declined' | 'call:ended' | 'call:error';
  session_id: string;
  data?: any;
  timestamp: number;
}

/**
 * SDP types for WebRTC signaling
 */
export interface SDPMessage {
  type: 'offer' | 'answer';
  sdp: string;
  session_id: string;
  offer?: RTCSessionDescriptionInit;
  answer?: RTCSessionDescriptionInit;
}

/**
 * ICE candidate message
 */
export interface ICECandidateMessage {
  candidate: RTCIceCandidateInit;
  session_id: string;
}

/**
 * Call metadata
 */
export interface CallMetadata {
  customer_id?: string;
  conversation_id?: string;
  agent_id?: string;
  start_time?: number;
  end_time?: number;
  duration?: number;
  quality_metrics?: {
    audio_level?: number;
    packet_loss?: number;
    jitter?: number;
  };
  incoming_call?: any; // Store incoming call data for acceptance/decline
}

/**
 * Error types for WebRTC operations
 */
export enum WebRTCError {
  PERMISSION_DENIED = 'permission_denied',
  NO_DEVICES_FOUND = 'no_devices_found',
  CONNECTION_FAILED = 'connection_failed',
  SIGNALING_ERROR = 'signaling_error',
  ICE_FAILURE = 'ice_failure',
  MEDIA_ERROR = 'media_error'
}

/**
 * Utility function to get user media with fallbacks
 */
export async function getMediaStream(constraints: MediaStreamConstraints = AUDIO_CONSTRAINTS): Promise<MediaStream> {
  try {
    // If requesting video+audio but video fails to enumerate, fall back to audio-only
    // before giving up entirely. NotFoundError can fire when there's no camera even
    // if a microphone exists.
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia(constraints);
    } catch (firstErr: any) {
      const isNotFound = firstErr?.name === 'NotFoundError' || firstErr?.name === 'DevicesNotFoundError';
      if (isNotFound && constraints.video) {
        // Retry audio-only
        console.warn('[getMediaStream] Camera not found, retrying audio-only');
        stream = await navigator.mediaDevices.getUserMedia({ audio: constraints.audio, video: false });
      } else {
        throw firstErr;
      }
    }

    // Validate that we actually got audio tracks
    if (constraints.audio && stream.getAudioTracks().length === 0) {
      throw new Error('No audio tracks available');
    }

    return stream;
  } catch (error: any) {
    console.error('[getMediaStream] Failed:', error?.name, error?.message);

    if (error?.name === 'NotAllowedError' || error?.name === 'PermissionDeniedError') {
      throw new Error(WebRTCError.PERMISSION_DENIED);
    }
    if (error?.name === 'NotFoundError' || error?.name === 'DevicesNotFoundError') {
      throw new Error(WebRTCError.NO_DEVICES_FOUND);
    }
    // Re-throw our own typed errors unchanged
    if (Object.values(WebRTCError).includes(error?.message)) {
      throw error;
    }
    throw new Error(WebRTCError.MEDIA_ERROR);
  }
}

/**
 * Utility function to create a unique session ID
 */
export function generateSessionId(): string {
  return `call_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Utility function to format connection state for display
 */
export function formatConnectionState(state: ConnectionState): string {
  switch (state) {
    case ConnectionState.NEW:
      return 'Preparing connection';
    case ConnectionState.CONNECTING:
      return 'Connecting...';
    case ConnectionState.CONNECTED:
      return 'Connected';
    case ConnectionState.DISCONNECTED:
      return 'Disconnected';
    case ConnectionState.FAILED:
      return 'Connection failed';
    case ConnectionState.CLOSED:
      return 'Connection closed';
    default:
      return 'Unknown state';
  }
}

/** Human-readable labels for WebRTC error codes. */
export function formatWebRTCError(error: string): string {
  const lower = error.toLowerCase();
  if (lower.includes('no_devices_found') || lower.includes('notfound'))
    return 'No microphone found. Check your device settings.';
  if (lower.includes('permission_denied') || lower.includes('notallowed'))
    return 'Microphone access denied. Allow browser permission and retry.';
  if (lower.includes('connection_failed'))
    return 'Connection failed. Check your network.';
  if (lower.includes('media_error'))
    return 'Could not access microphone.';
  return error;
}

/**
 * Utility function to format call state for display
 */
export function formatCallState(state: CallState): string {
  switch (state) {
    case CallState.IDLE:
      return 'Ready';
    case CallState.INITIATING:
      return 'Calling...';
    case CallState.RINGING:
      return 'Ringing...';
    case CallState.CONNECTING:
      return 'Connecting...';
    case CallState.CONNECTED:
      return 'Connected';
    case CallState.RECONNECTING:
      return 'Reconnecting...';
    case CallState.ENDED:
      return 'Call ended';
    case CallState.ERROR:
      return 'Error';
    default:
      return 'Unknown state';
  }
}
